/**
 * Match review — the analyst gate between remediation and synthesis.
 *
 * The backend queues the remediation decisions it is not sure about as
 * `remediation_reviews` rows; an admin records a verdict on each and
 * submits, which re-runs remediation with the verdicts pinned where they
 * overrode the AI and lets the report continue.
 */

import type { MatchReviewMode, ReportStatus } from './report';

export type MatchReviewReason =
  | 'low_confidence_match'
  | 'uncertain_non_match'
  | 'cross_validation_demotion'
  | 'retained_match';

export type MatchReviewDecision = 'confirm_match' | 'reject_match';

export type AiMatchDecision = 'true_positive' | 'false_positive';

/** Condensed record card built by the remediation graph so the UI never
 * opens the S3 blobs. Keys are whatever the provider extraction found. */
export interface MatchReviewCard {
  provider?: string;
  type?: string;
  name?: string;
  jurisdictions?: string[];
  key_facts?: string[];
  source_url?: string | null;
}

export interface ApiRemediationReview {
  id: string;
  report_id: string;
  subject_id: string;
  remediation_run_prefix: string;
  record_id: string;
  raw_data_hash: string;
  provider: string;
  record_type: string;
  variant_used: string | null;
  ai_decision: AiMatchDecision;
  ai_confidence: number;
  ai_match_confidence: number;
  ai_reasoning: string;
  review_reason: MatchReviewReason;
  summary: MatchReviewCard;
  analyst_decision: MatchReviewDecision | null;
  decided_by_user_id: string | null;
  decided_at: string | null;
  note: string | null;
  applied_at: string | null;
  superseded_at: string | null;
  created_at: string | null;
}

export interface RemediationReview {
  id: string;
  reportId: string;
  subjectId: string;
  recordId: string;
  rawDataHash: string;
  provider: string;
  recordType: string;
  variantUsed: string | null;
  aiDecision: AiMatchDecision;
  aiConfidence: number;
  /** Normalised probability that the record refers to the subject. */
  aiMatchConfidence: number;
  aiReasoning: string;
  reviewReason: MatchReviewReason;
  summary: MatchReviewCard;
  analystDecision: MatchReviewDecision | null;
  decidedByUserId: string | null;
  decidedAt: string | null;
  note: string | null;
  appliedAt: string | null;
  supersededAt: string | null;
  createdAt: string | null;
}

export interface ApiSubjectMatchReviews {
  subject_id: string;
  subject_name: string;
  reviews: ApiRemediationReview[];
}

export interface ApiMatchReviewList {
  report_id: string;
  report_status: ReportStatus;
  mode: MatchReviewMode;
  threshold: number;
  pending_count: number;
  undecided_count: number;
  subjects: ApiSubjectMatchReviews[];
}

export interface SubjectMatchReviews {
  subjectId: string;
  subjectName: string;
  reviews: RemediationReview[];
}

export interface MatchReviewList {
  reportId: string;
  reportStatus: ReportStatus;
  mode: MatchReviewMode;
  threshold: number;
  /** Rows still gating the report (not applied, not superseded). */
  pendingCount: number;
  /** Pending rows without a verdict yet — submit is blocked while > 0. */
  undecidedCount: number;
  subjects: SubjectMatchReviews[];
}

export interface ApiMatchReviewSubmitResponse {
  next_action: string | null;
  report_status: ReportStatus;
  rerun_subject_ids: string[];
}

export interface MatchReviewSubmitResponse {
  nextAction: string | null;
  reportStatus: ReportStatus;
  rerunSubjectIds: string[];
}

export function transformToRemediationReview(api: ApiRemediationReview): RemediationReview {
  return {
    id: api.id,
    reportId: api.report_id,
    subjectId: api.subject_id,
    recordId: api.record_id,
    rawDataHash: api.raw_data_hash,
    provider: api.provider,
    recordType: api.record_type,
    variantUsed: api.variant_used ?? null,
    aiDecision: api.ai_decision,
    aiConfidence: api.ai_confidence,
    aiMatchConfidence: api.ai_match_confidence,
    aiReasoning: api.ai_reasoning ?? '',
    reviewReason: api.review_reason,
    summary: api.summary ?? {},
    analystDecision: api.analyst_decision ?? null,
    decidedByUserId: api.decided_by_user_id ?? null,
    decidedAt: api.decided_at ?? null,
    note: api.note ?? null,
    appliedAt: api.applied_at ?? null,
    supersededAt: api.superseded_at ?? null,
    createdAt: api.created_at ?? null,
  };
}

export function transformToMatchReviewList(api: ApiMatchReviewList): MatchReviewList {
  return {
    reportId: api.report_id,
    reportStatus: api.report_status,
    mode: api.mode,
    threshold: api.threshold,
    pendingCount: api.pending_count,
    undecidedCount: api.undecided_count,
    subjects: api.subjects.map((s) => ({
      subjectId: s.subject_id,
      subjectName: s.subject_name,
      reviews: s.reviews.map(transformToRemediationReview),
    })),
  };
}

export function transformToMatchReviewSubmitResponse(
  api: ApiMatchReviewSubmitResponse
): MatchReviewSubmitResponse {
  return {
    nextAction: api.next_action ?? null,
    reportStatus: api.report_status,
    rerunSubjectIds: api.rerun_subject_ids ?? [],
  };
}
